// JSON boundary adapter for the shared radio/channel model.
// This module owns JSON shape validation and import-time compatibility mapping
// for older field forms. After that boundary work, it delegates canonical cleanup
// to the shared normalizers so runtime code can operate on one consistent model.
import {
  type CrossBandMode,
  type DualWatchMode,
  type PowerOnDisplayMode,
  type RogerMode,
  type ScanTargetMode,
  type ScanResumeMode,
  createDefaultRadioState,
  type ChannelModel,
  type RadioStateModel
} from "../types/radio-types";
import {
  sanitizeScanDwellTime,
  sanitizeTelemetryPeakTime
} from "../radio/radio-sanitizers";
import { TELEMETRY_MIN_FREQUENCY } from "../radio/channel-normalization";
import { CHANNEL_COUNT, VISIBLE_SCAN_LIST_COUNT } from "../radio/radio-layout";
import { STEP_INDEX_0_5_KHZ } from "../radio/step-table";
import { normalizeRadio } from "../radio/radio-normalization";
import { normalizeChannels } from "../radio/channel-normalization";

type JsonObject = Record<string, unknown>;

interface RadioParseNodes {
  radioObj: JsonObject;
  activeNode: JsonObject;
  legacyNode: JsonObject;
  activeScanNode: JsonObject;
  activeTelemetryNode: JsonObject;
  activeDisplayNode: JsonObject;
  activeReceiverNode: JsonObject;
  activeBootNode: JsonObject;
  activeKeysNode: JsonObject;
  activeMeterNode: JsonObject;
  activeDeveloperNode: JsonObject;
}

export interface ParsedConfigFile {
  channels: ChannelModel[];
  radio: RadioStateModel;
  /** Optional source receiver UID, normalized to 24 uppercase hex characters. */
  deviceId?: string;
}

export interface ConfigExportOptions {
  /** Include protected calibration/developer fields in the exported file. */
  includeRadioSpecific?: boolean;
  /** Source receiver UID. It is emitted only with radio-specific content. */
  deviceId?: string;
}

// Basic JSON validators stay intentionally small and repetitive so the boundary
// can fail with precise path-specific error messages.
function asObject(value: unknown, context: string): JsonObject {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${context} must be an object`);
  }
  return value as JsonObject;
}

function asArray(value: unknown, context: string): unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`${context} must be an array`);
  }
  return value;
}

function asNumber(value: unknown, context: string): number {
  if (typeof value !== "number" || Number.isNaN(value)) {
    throw new Error(`${context} must be a number`);
  }
  return value;
}

function asInteger(value: unknown, context: string): number {
  const num = asNumber(value, context);
  if (!Number.isInteger(num)) {
    throw new Error(`${context} must be an integer`);
  }
  return num;
}

function asBoolean(value: unknown, context: string): boolean {
  if (typeof value !== "boolean") {
    throw new Error(`${context} must be a boolean`);
  }
  return value;
}

function asString(value: unknown, context: string): string {
  if (typeof value !== "string") {
    throw new Error(`${context} must be a string`);
  }
  return value;
}

function readOptionalDeviceId(value: unknown, context: string): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  const deviceId = asString(value, context).trim().toUpperCase();
  if (!/^[0-9A-F]{24}$/.test(deviceId)) {
    throw new Error(`${context} must be exactly 24 hexadecimal characters`);
  }
  return deviceId;
}

function cloneJsonObject(value: JsonObject): JsonObject {
  return JSON.parse(JSON.stringify(value)) as JsonObject;
}

function readOptionalEnum<T extends string>(
  obj: JsonObject,
  key: string,
  context: string,
  allowed: readonly T[]
): T | undefined {
  const value = obj[key];
  if (value === undefined) {
    return undefined;
  }
  const text = asString(value, `${context}.${key}`);
  if (!allowed.includes(text as T)) {
    throw new Error(`${context}.${key} must be one of: ${allowed.join(", ")}`);
  }
  return text as T;
}

function readOptionalBoolean(obj: JsonObject, key: string, context: string): boolean | undefined {
  const value = obj[key];
  if (value === undefined) {
    return undefined;
  }
  return asBoolean(value, `${context}.${key}`);
}

function readOptionalInteger(obj: JsonObject, key: string, context: string): number | undefined {
  const value = obj[key];
  if (value === undefined) {
    return undefined;
  }
  return asInteger(value, `${context}.${key}`);
}

// Gather all radio sub-objects once so compatibility helpers can stay focused on
// policy rather than repeated object-shape plumbing.
function getRadioParseNodes(radioNode: unknown): RadioParseNodes {
  const radioObj = asObject(radioNode, "root.radio");
  const activeNode =
    radioObj.active_options === undefined
      ? {}
      : asObject(radioObj.active_options, "root.radio.active_options");
  const legacyNode =
    radioObj.legacy_values === undefined
      ? {}
      : asObject(radioObj.legacy_values, "root.radio.legacy_values");

  return {
    radioObj,
    activeNode,
    legacyNode,
    activeScanNode:
      activeNode.scan === undefined
        ? {}
        : asObject(activeNode.scan, "root.radio.active_options.scan"),
    activeTelemetryNode:
      activeNode.telemetry === undefined
        ? {}
        : asObject(activeNode.telemetry, "root.radio.active_options.telemetry"),
    activeDisplayNode:
      activeNode.display === undefined
        ? {}
        : asObject(activeNode.display, "root.radio.active_options.display"),
    activeReceiverNode:
      activeNode.receiver === undefined
        ? {}
        : asObject(activeNode.receiver, "root.radio.active_options.receiver"),
    activeBootNode:
      activeNode.boot === undefined
        ? {}
        : asObject(activeNode.boot, "root.radio.active_options.boot"),
    activeKeysNode:
      activeNode.keys === undefined
        ? {}
        : asObject(activeNode.keys, "root.radio.active_options.keys"),
    activeMeterNode:
      activeNode.meter_calibration === undefined
        ? {}
        : asObject(activeNode.meter_calibration, "root.radio.active_options.meter_calibration"),
    activeDeveloperNode:
      activeNode.developer === undefined
        ? {}
        : asObject(activeNode.developer, "root.radio.active_options.developer")
  };
}

// Import compatibility: older JSON could store squelch under receiver instead of developer.
function readImportedSquelchLevel(nodes: RadioParseNodes): number | undefined {
  return (
    readOptionalInteger(
      nodes.activeDeveloperNode,
      "squelch_level",
      "root.radio.active_options.developer"
    ) ??
    readOptionalInteger(
      nodes.activeReceiverNode,
      "squelch_level",
      "root.radio.active_options.receiver"
    )
  );
}

function readImportedScanTargetMode(nodes: RadioParseNodes): ScanTargetMode | undefined {
  return readOptionalEnum<ScanTargetMode>(
    nodes.activeScanNode,
    "target_mode",
    "root.radio.active_options.scan",
    ["lists", "all"]
  );
}

// Import compatibility: older JSON represented the active scan list indirectly.
function readImportedLegacyDefaultList(nodes: RadioParseNodes): string | undefined {
  return readOptionalEnum<string>(
    nodes.activeScanNode,
    "default_list",
    "root.radio.active_options.scan",
    ["list1", "list2", "list3", "list4", "list5", "all"]
  );
}

function readImportedActiveListMask(
  nodes: RadioParseNodes,
  defaults: RadioStateModel,
  importedScanTargetMode: ScanTargetMode | undefined,
  importedLegacyDefaultList: string | undefined
): number {
  if (importedScanTargetMode === "all") {
    return 0;
  }
  const explicitMask = readOptionalInteger(
    nodes.activeScanNode,
    "active_list_mask",
    "root.radio.active_options.scan"
  );
  if (explicitMask !== undefined) {
    return explicitMask & 0xffff;
  }
  if (importedLegacyDefaultList && importedLegacyDefaultList !== "all") {
    const idx = Number(importedLegacyDefaultList.replace("list", "")) - 1;
    if (idx >= 0 && idx < VISIBLE_SCAN_LIST_COUNT) {
      return 1 << idx;
    }
  }
  if (importedScanTargetMode === "lists") {
    return 1;
  }
  return defaults.activeOptions.scan.active_list_mask;
}

function readImportedListNames(nodes: RadioParseNodes, defaults: RadioStateModel): string[] {
  const namesNode = nodes.activeScanNode.list_names;
  if (namesNode === undefined) {
    return [...defaults.activeOptions.scan.list_names];
  }
  const values = asArray(namesNode, "root.radio.active_options.scan.list_names");
  const names = [...defaults.activeOptions.scan.list_names];
  for (let i = 0; i < Math.min(VISIBLE_SCAN_LIST_COUNT, values.length); i += 1) {
    names[i] = asString(values[i], `root.radio.active_options.scan.list_names[${i}]`);
  }
  return names;
}

// Import compatibility: older JSON used "full_screen" for what is now "logo".
function readImportedPowerOnDisplayMode(
  nodes: RadioParseNodes,
  defaults: RadioStateModel
): PowerOnDisplayMode {
  const value = readOptionalEnum<string>(
    nodes.activeDisplayNode,
    "power_on_display_mode",
    "root.radio.active_options.display",
    ["full_screen", "message", "sound", "voltage", "logo", "none"]
  );
  if (value === "full_screen") {
    return "logo";
  }
  if (
    value === "message" ||
    value === "sound" ||
    value === "voltage" ||
    value === "logo" ||
    value === "none"
  ) {
    return value;
  }
  return defaults.activeOptions.display.power_on_display_mode;
}

// Import compatibility: older JSON exposed a more device-specific flashlight key field.
function readImportedFlashlightEnabled(
  nodes: RadioParseNodes,
  defaults: RadioStateModel
): boolean {
  const explicit = readOptionalBoolean(
    nodes.activeKeysNode,
    "flashlight_enabled",
    "root.radio.active_options.keys"
  );
  if (explicit !== undefined) {
    return explicit;
  }
  const legacyKey = readOptionalEnum<string>(
    nodes.activeKeysNode,
    "flashlight_key",
    "root.radio.active_options.keys",
    ["none", "m_long", "f1_long", "f2_long"]
  );
  if (legacyKey !== undefined) {
    return legacyKey !== "none";
  }
  return defaults.activeOptions.keys.flashlight_enabled;
}

// Legacy radio values are still parsed for near-term compatibility, but remain
// outside the canonical active-options model.
function parseLegacyValues(legacyNode: JsonObject, defaults: RadioStateModel) {
  const rawNode =
    legacyNode.raw === undefined ? {} : asObject(legacyNode.raw, "root.radio.legacy_values.raw");

  return {
    tx_timeout_timer:
      readOptionalInteger(legacyNode, "tx_timeout_timer", "root.radio.legacy_values") ??
      defaults.legacyValues.tx_timeout_timer,
    mic_sensitivity:
      readOptionalInteger(legacyNode, "mic_sensitivity", "root.radio.legacy_values") ??
      defaults.legacyValues.mic_sensitivity,
    roger_mode:
      readOptionalEnum<RogerMode>(
        legacyNode,
        "roger_mode",
        "root.radio.legacy_values",
        ["off", "roger", "mdc"]
      ) ?? defaults.legacyValues.roger_mode,
    repeater_tail_tone_elimination:
      readOptionalInteger(
        legacyNode,
        "repeater_tail_tone_elimination",
        "root.radio.legacy_values"
      ) ?? defaults.legacyValues.repeater_tail_tone_elimination,
    tx_vfo_index:
      readOptionalInteger(legacyNode, "tx_vfo_index", "root.radio.legacy_values") ??
      defaults.legacyValues.tx_vfo_index,
    auto_keypad_lock:
      readOptionalBoolean(legacyNode, "auto_keypad_lock", "root.radio.legacy_values") ??
      defaults.legacyValues.auto_keypad_lock,
    cross_band_rx_tx:
      readOptionalEnum<CrossBandMode>(
        legacyNode,
        "cross_band_rx_tx",
        "root.radio.legacy_values",
        ["off", "chan_a", "chan_b"]
      ) ?? defaults.legacyValues.cross_band_rx_tx,
    dual_watch:
      readOptionalEnum<DualWatchMode>(
        legacyNode,
        "dual_watch",
        "root.radio.legacy_values",
        ["off", "chan_a", "chan_b"]
      ) ?? defaults.legacyValues.dual_watch,
    vfo_open:
      readOptionalBoolean(legacyNode, "vfo_open", "root.radio.legacy_values") ??
      defaults.legacyValues.vfo_open,
    raw: cloneJsonObject(rawNode)
  };
}

// Channel import compatibility helpers mirror the radio ones above so the file
// parser has a clear "map old forms, then normalize" structure.
function readLegacyScanlistMask(attributes: JsonObject, context: string): number | undefined {
  if (attributes.scanlist_mask === undefined) {
    return undefined;
  }
  const value = asInteger(attributes.scanlist_mask, `${context}.scanlist_mask`);
  if (value < 0 || value > 0xffff) {
    throw new Error(`${context}.scanlist_mask must be in range 0..65535`);
  }
  return value;
}

function readLegacyBooleanScanlists(attributes: JsonObject, context: string): number {
  let mask = 0;
  if (attributes.scanlist1 !== undefined && asBoolean(attributes.scanlist1, `${context}.scanlist1`)) {
    mask |= 1 << 0;
  }
  if (attributes.scanlist2 !== undefined && asBoolean(attributes.scanlist2, `${context}.scanlist2`)) {
    mask |= 1 << 1;
  }
  return mask & 0xffff;
}

function parseChannelLegacy(entry: JsonObject, idx: number): {
  stepSetting: number;
  bandwidth: "wide" | "narrow";
} {
  let stepSetting = 0;
  let bandwidth: "wide" | "narrow" = "narrow";

  if (entry.legacy === undefined) {
    return { stepSetting, bandwidth };
  }

  const legacy = asObject(entry.legacy, `channels[${idx}].legacy`);
  if (legacy.step_setting !== undefined) {
    stepSetting = asNumber(legacy.step_setting, `channels[${idx}].legacy.step_setting`);
  }
  if (legacy.bandwidth !== undefined) {
    const bw = asString(legacy.bandwidth, `channels[${idx}].legacy.bandwidth`);
    if (bw === "wide" || bw === "narrow") {
      bandwidth = bw;
    } else {
      throw new Error(`channels[${idx}].legacy.bandwidth must be 'wide' or 'narrow'`);
    }
  }

  return { stepSetting, bandwidth };
}

// Parse JSON into the current field set, then normalize once into canonical
// radio state. This keeps compatibility mapping and runtime invariants separate.
function parseRadioState(radioNode: unknown, importDefaults: RadioStateModel): RadioStateModel {
  const defaults = importDefaults;
  if (radioNode === undefined) {
    return defaults;
  }

  const nodes = getRadioParseNodes(radioNode);
  // Compatibility note:
  // - New exports no longer emit radio.legacy_values or meter_calibration.
  // - New exports also emit squelch_level under developer, not receiver.
  // We still accept those older forms on import for now so previously saved
  // files and generated payloads continue to load. When we remove these
  // fallback reads, update the JSON spec appendices at the same time.
  const importedSquelchLevel = readImportedSquelchLevel(nodes);
  const importedScanTargetMode = readImportedScanTargetMode(nodes);
  const importedLegacyDefaultList = readImportedLegacyDefaultList(nodes);

  const parsedActiveOptions = {
    beep_control:
      readOptionalBoolean(nodes.activeNode, "beep_control", "root.radio.active_options") ??
      readOptionalBoolean(nodes.legacyNode, "beep_control", "root.radio.legacy_values") ??
      defaults.activeOptions.beep_control,
    bpm_control:
      readOptionalBoolean(nodes.activeNode, "bpm_control", "root.radio.active_options") ??
      readOptionalBoolean(nodes.legacyNode, "bpm_control", "root.radio.legacy_values") ??
      defaults.activeOptions.bpm_control,
    // This optional file field intentionally defaults off rather than
    // inheriting a receiver value when it is absent from the JSON document.
    display_strength:
      readOptionalBoolean(nodes.activeNode, "display_strength", "root.radio.active_options") ?? false,
    battery_type:
      readOptionalInteger(nodes.activeNode, "battery_type", "root.radio.active_options") ??
      readOptionalInteger(nodes.legacyNode, "battery_type", "root.radio.legacy_values") ??
      defaults.activeOptions.battery_type,
    scan: {
      active_list_mask: readImportedActiveListMask(
        nodes,
        defaults,
        importedScanTargetMode,
        importedLegacyDefaultList
      ),
      list_names: readImportedListNames(nodes, defaults),
      resume_mode:
        readOptionalEnum<ScanResumeMode>(
          nodes.activeScanNode,
          "resume_mode",
          "root.radio.active_options.scan",
          ["stop", "dwell"]
        ) ?? defaults.activeOptions.scan.resume_mode,
      dwell_time_seconds:
        readOptionalInteger(
          nodes.activeScanNode,
          "dwell_time_seconds",
          "root.radio.active_options.scan"
        ) ?? defaults.activeOptions.scan.dwell_time_seconds
    },
    telemetry: {
      peak_time:
        readOptionalInteger(
          nodes.activeTelemetryNode,
          "peak_time",
          "root.radio.active_options.telemetry"
        ) ?? defaults.activeOptions.telemetry.peak_time
    },
    receiver: {
      squelch_level: (() => {
        return importedSquelchLevel ?? defaults.activeOptions.receiver.squelch_level;
      })(),
      rf_gain:
        readOptionalInteger(
          nodes.activeReceiverNode,
          "rf_gain",
          "root.radio.active_options.receiver"
        ) ?? defaults.activeOptions.receiver.rf_gain,
      navigation_mode: (() => {
        const explicit = readOptionalEnum<"channel" | "id">(
          nodes.activeReceiverNode,
          "navigation_mode",
          "root.radio.active_options.receiver",
          ["channel", "id"]
        );
        if (explicit !== undefined) {
          return explicit;
        }
        return defaults.activeOptions.receiver.navigation_mode;
      })()
    },
    display: {
      power_on_display_mode: readImportedPowerOnDisplayMode(nodes, defaults),
      backlight_time:
        readOptionalInteger(
          nodes.activeDisplayNode,
          "backlight_time",
          "root.radio.active_options.display"
        ) ?? defaults.activeOptions.display.backlight_time
    },
    boot: {
      line1:
        (nodes.activeBootNode.line1 === undefined
          ? defaults.activeOptions.boot.line1
          : asString(nodes.activeBootNode.line1, "root.radio.active_options.boot.line1")),
      line2:
        (nodes.activeBootNode.line2 === undefined
          ? defaults.activeOptions.boot.line2
          : asString(nodes.activeBootNode.line2, "root.radio.active_options.boot.line2"))
    },
    keys: {
      flashlight_enabled: readImportedFlashlightEnabled(nodes, defaults),
      arrow_orientation: (() => {
        const explicit = readOptionalEnum<"up_down" | "left_right">(
          nodes.activeKeysNode,
          "arrow_orientation",
          "root.radio.active_options.keys",
          ["up_down", "left_right"]
        );
        return explicit ?? defaults.activeOptions.keys.arrow_orientation;
      })()
    },
    meter_calibration: {
      s0_level:
        readOptionalInteger(
          nodes.activeMeterNode,
          "s0_level",
          "root.radio.active_options.meter_calibration"
        ) ?? defaults.activeOptions.meter_calibration.s0_level,
      s9_level:
        readOptionalInteger(
          nodes.activeMeterNode,
          "s9_level",
          "root.radio.active_options.meter_calibration"
        ) ?? defaults.activeOptions.meter_calibration.s9_level
    },
    developer: {
      pulse_threshold_dbm:
        readOptionalInteger(
          nodes.activeDeveloperNode,
          "pulse_threshold_dbm",
          "root.radio.active_options.developer"
        ) ?? defaults.activeOptions.developer.pulse_threshold_dbm,
      full_scale_dbm:
        readOptionalInteger(
          nodes.activeDeveloperNode,
          "full_scale_dbm",
          "root.radio.active_options.developer"
        ) ?? defaults.activeOptions.developer.full_scale_dbm,
      frequency_offset_hz:
        readOptionalInteger(
          nodes.activeDeveloperNode,
          "frequency_offset_hz",
          "root.radio.active_options.developer"
        ) ?? defaults.activeOptions.developer.frequency_offset_hz,
      force_bandwidth:
        nodes.activeDeveloperNode.force_bandwidth === "dont_force" ||
        nodes.activeDeveloperNode.force_bandwidth === "wide" ||
        nodes.activeDeveloperNode.force_bandwidth === "narrow" ||
        nodes.activeDeveloperNode.force_bandwidth === "narrower"
          ? nodes.activeDeveloperNode.force_bandwidth
          : defaults.activeOptions.developer.force_bandwidth,
      battery_calibration:
        readOptionalInteger(
          nodes.activeDeveloperNode,
          "battery_calibration",
          "root.radio.active_options.developer"
        ) ?? defaults.activeOptions.developer.battery_calibration,
      present: {
        squelch_level: importedSquelchLevel !== undefined,
        pulse_threshold_dbm: nodes.activeDeveloperNode.pulse_threshold_dbm !== undefined,
        full_scale_dbm: nodes.activeDeveloperNode.full_scale_dbm !== undefined,
        frequency_offset_hz: nodes.activeDeveloperNode.frequency_offset_hz !== undefined,
        force_bandwidth: nodes.activeDeveloperNode.force_bandwidth !== undefined,
        battery_calibration: nodes.activeDeveloperNode.battery_calibration !== undefined
      }
    }
  };

  const parsedLegacyValues = parseLegacyValues(nodes.legacyNode, defaults);

  return normalizeRadio({
    activeOptions: parsedActiveOptions,
    legacyValues: parsedLegacyValues
  }).radio;
}

function parseScanlistMask(attributes: JsonObject, idx: number): number {
  const context = `channels[${idx}].attributes`;

  if (attributes.scanlists !== undefined) {
    const entries = asArray(attributes.scanlists, `${context}.scanlists`);
    let mask = 0;
    for (let i = 0; i < entries.length; i += 1) {
      const listNumber = asInteger(entries[i], `${context}.scanlists[${i}]`);
      if (listNumber < 1 || listNumber > VISIBLE_SCAN_LIST_COUNT) {
        throw new Error(`${context}.scanlists[${i}] must be in range 1..${VISIBLE_SCAN_LIST_COUNT}`);
      }
      mask |= 1 << (listNumber - 1);
    }
    return mask & 0xffff;
  }

  const legacyMask = readLegacyScanlistMask(attributes, context);
  if (legacyMask !== undefined) {
    return legacyMask;
  }

  return readLegacyBooleanScanlists(attributes, context);
}

// Build a draft channel model from JSON field names and legacy aliases. The
// caller is responsible for running channel normalization afterward.
function parseChannel(entry: unknown, idx: number): ChannelModel {
  const obj = asObject(entry, `channels[${idx}]`);
  const attributes = asObject(obj.attributes, `channels[${idx}].attributes`);

  const index = asInteger(obj.index, `channels[${idx}].index`);
  const active = asBoolean(obj.active, `channels[${idx}].active`);
  const name = asString(obj.name, `channels[${idx}].name`);
  const rxFrequency = asNumber(obj.rx_frequency, `channels[${idx}].rx_frequency`);
  const band = asInteger(attributes.band, `channels[${idx}].attributes.band`);
  const scanlistMask = parseScanlistMask(attributes, idx);
  const legacyValues = parseChannelLegacy(obj, idx);
  let bandwidth: "wide" | "narrow" = legacyValues.bandwidth;
  let stepSetting = legacyValues.stepSetting;

  if (obj.step_setting !== undefined) {
    stepSetting = asNumber(obj.step_setting, `channels[${idx}].step_setting`);
  }

  return {
    index,
    active,
    name,
    rxFrequency,
    band,
    scanlistMask,
    stepSetting,
    bandwidth
  };
}

function buildInactiveChannel(index: number): ChannelModel {
  return {
    index,
    active: false,
    name: "",
    rxFrequency: TELEMETRY_MIN_FREQUENCY,
    band: 7,
    scanlistMask: 0,
    stepSetting: STEP_INDEX_0_5_KHZ,
    bandwidth: "narrow"
  };
}

function expandChannelsToReceiverModel(parsedChannels: ChannelModel[]): ChannelModel[] {
  const channels = Array.from({ length: CHANNEL_COUNT }, (_, index) => buildInactiveChannel(index));

  for (const channel of parsedChannels) {
    if (!Number.isInteger(channel.index) || channel.index < 0 || channel.index >= CHANNEL_COUNT) {
      throw new Error(`channels[${channel.index}] index must be in range 0..${CHANNEL_COUNT - 1}`);
    }
    channels[channel.index] = channel;
  }

  return channels;
}

/**
 * Parses a configuration file, filling omitted radio fields from the supplied
 * startup default. Callers without a startup profile use the built-in fallback.
 */
export function parseConfigFileText(text: string, importDefaults = createDefaultRadioState()): ParsedConfigFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new Error(
      `Invalid JSON: ${error instanceof Error ? error.message : String(error)}`
    );
  }

  const root = asObject(parsed, "root");
  if (!Array.isArray(root.channels)) {
    throw new Error("root.channels must be an array");
  }

  const radio = parseRadioState(root.radio, importDefaults);

  return {
    channels: expandChannelsToReceiverModel(root.channels.map((entry, idx) => parseChannel(entry, idx))),
    radio,
    deviceId: readOptionalDeviceId(root.device_id, "root.device_id")
  };
}

export function parseChannelsFileText(text: string): ChannelModel[] {
  return parseConfigFileText(text).channels;
}

// JSON export assumes callers want the current canonical schema, so it first
// normalizes both models and then serializes from that canonical state.
export function buildConfigFileJson(
  channels: ChannelModel[],
  radio: RadioStateModel,
  options: ConfigExportOptions = {}
): string {
  const normalizedRadio = normalizeRadio(radio).radio;
  const normalizedChannels = normalizeChannels(channels).channels;
  // JSON import expands omitted indices as inactive, so exports can omit
  // inactive slots while still producing a complete desired channel set.
  const exportedChannels = normalizedChannels.filter((channel) => channel.active);

  const receiverNode: Record<string, unknown> = {
    rf_gain: normalizedRadio.activeOptions.receiver.rf_gain,
    navigation_mode: normalizedRadio.activeOptions.receiver.navigation_mode
  };

  const developerNode: Record<string, unknown> = {};
  const includeRadioSpecific = options.includeRadioSpecific !== false;
  const deviceId = options.deviceId === undefined
    ? undefined
    : readOptionalDeviceId(options.deviceId, "device_id");
  if (includeRadioSpecific && normalizedRadio.activeOptions.developer.present.squelch_level) {
    developerNode.squelch_level = normalizedRadio.activeOptions.receiver.squelch_level;
  }
  if (includeRadioSpecific && normalizedRadio.activeOptions.developer.present.pulse_threshold_dbm) {
    developerNode.pulse_threshold_dbm = normalizedRadio.activeOptions.developer.pulse_threshold_dbm;
  }
  if (includeRadioSpecific && normalizedRadio.activeOptions.developer.present.full_scale_dbm) {
    developerNode.full_scale_dbm = normalizedRadio.activeOptions.developer.full_scale_dbm;
  }
  if (includeRadioSpecific && normalizedRadio.activeOptions.developer.present.frequency_offset_hz) {
    developerNode.frequency_offset_hz = normalizedRadio.activeOptions.developer.frequency_offset_hz;
  }
  if (includeRadioSpecific && normalizedRadio.activeOptions.developer.present.force_bandwidth) {
    developerNode.force_bandwidth = normalizedRadio.activeOptions.developer.force_bandwidth;
  }
  if (includeRadioSpecific && normalizedRadio.activeOptions.developer.present.battery_calibration) {
    developerNode.battery_calibration = normalizedRadio.activeOptions.developer.battery_calibration;
  }

  const payload = {
    schema_version: 11,
    radio_model: "uv-k5-telemetry",
    ...(includeRadioSpecific && deviceId ? { device_id: deviceId } : {}),
    radio: {
      active_options: {
        beep_control: normalizedRadio.activeOptions.beep_control,
        bpm_control: normalizedRadio.activeOptions.bpm_control,
        display_strength: normalizedRadio.activeOptions.display_strength,
        battery_type: normalizedRadio.activeOptions.battery_type,
        scan: {
          active_list_mask: normalizedRadio.activeOptions.scan.active_list_mask,
          list_names: normalizedRadio.activeOptions.scan.list_names.slice(0, VISIBLE_SCAN_LIST_COUNT),
          resume_mode: normalizedRadio.activeOptions.scan.resume_mode,
          dwell_time_seconds: normalizedRadio.activeOptions.scan.dwell_time_seconds
        },
        telemetry: {
          peak_time: normalizedRadio.activeOptions.telemetry.peak_time
        },
        receiver: receiverNode,
        display: {
          power_on_display_mode: normalizedRadio.activeOptions.display.power_on_display_mode,
          backlight_time: normalizedRadio.activeOptions.display.backlight_time
        },
        boot: {
          line1: normalizedRadio.activeOptions.boot.line1,
          line2: normalizedRadio.activeOptions.boot.line2
        },
        keys: {
          flashlight_enabled: normalizedRadio.activeOptions.keys.flashlight_enabled,
          arrow_orientation: normalizedRadio.activeOptions.keys.arrow_orientation
        },
        ...(Object.keys(developerNode).length > 0 ? { developer: developerNode } : {})
      }
    },
    channel_count: normalizedChannels.length,
    channels: exportedChannels.map((channel) => ({
      index: channel.index,
      active: channel.active,
      name: channel.name,
      rx_frequency: channel.rxFrequency,
      step_setting: channel.stepSetting,
      attributes: {
        band: channel.band,
        scanlists: Array.from({ length: VISIBLE_SCAN_LIST_COUNT }, (_, i) => i + 1).filter(
          (listNumber) => (channel.scanlistMask & (1 << (listNumber - 1))) !== 0
        )
      }
    }))
  };

  return `${JSON.stringify(payload, null, 2)}\n`;
}

export function buildChannelsFileJson(channels: ChannelModel[]): string {
  return buildConfigFileJson(channels, createDefaultRadioState());
}
